import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Grid, Stack, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

import {
  ICourseTutorForAdmin,
  getCourseTutorsAsAdmin,
  getCourseTutorsState,
} from '../../../../../../../store/V2/CourseTutorsSlice';
import { getCoursesState } from '../../../../../../../store/V2/CoursesSlice';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../../../store/UserSlice';
import { getAuthState } from '../../../../../../../store/AuthSlice';
import { useAppSelector } from '../../../../../../../hooks';
import { checkUserPermission } from '../../../../../../../helper';
import AddCourseTutorModal from './modals/AddCourseTutorModal';
import BasicLoader from '../../../../../../../components/Loaders/BasicLoader';
import CourseTutorCard from './components/CourseTutorCard';

type TEditCourseTutorModalData = {
  open: boolean;
  courseTutor?: ICourseTutorForAdmin;
};

interface TutorsTabProps {
  onClose: () => void;
}

const TutorsTab: React.FC<TutorsTabProps> = ({ onClose }) => {
  const dispatch = useDispatch();

  // Store
  const coursesState = useAppSelector(getCoursesState);
  const courseTutorsState = useAppSelector(getCourseTutorsState);
  const authState = useAppSelector(getAuthState);

  // State
  const [isAddCourseTutorModalOpen, setAddCourseTutorModalOpen] = useState(
    false
  );

  const [
    editCourseTutorModalData,
    setEditCourseTutorModalData,
  ] = useState<TEditCourseTutorModalData>({
    open: false,
  });

  const courseData = coursesState.adminCourse;
  const { adminCourseTutorsData } = courseTutorsState;

  let isAddButtonVisible =
    checkUserPermission(authState.user, {
      adminCourse: [USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT],
    }) && adminCourseTutorsData;

  if (
    courseData?.isOneToOne &&
    adminCourseTutorsData &&
    adminCourseTutorsData.totalDocs &&
    adminCourseTutorsData.totalDocs > 0
  ) {
    isAddButtonVisible = false;
  }

  useEffect(() => {
    if (courseData) {
      dispatch(
        getCourseTutorsAsAdmin({
          course: courseData._id,
          limit: 200,
          offset: 0,
        })
      );
    }
  }, []);

  const openCourseTutorEditModal = (courseTutor: ICourseTutorForAdmin) => {
    setEditCourseTutorModalData({
      courseTutor,
      open: true,
    });
  };

  const closeCourseTutorEditModal = () => {
    setEditCourseTutorModalData({
      open: false,
    });
  };

  const toggleAddCourseTutorModal = () => {
    setAddCourseTutorModalOpen(!isAddCourseTutorModalOpen);
  };

  return (
    <div style={{ padding: '50px 30px 30px 30px' }}>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        mb="10px"
      >
        {isAddButtonVisible ? (
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={toggleAddCourseTutorModal}
          >
            Add
          </Button>
        ) : null}
      </Stack>
      <div>
        <Grid container spacing={2}>
          {courseTutorsState.status === 'loading' && !adminCourseTutorsData ? (
            <Grid item xs={12}>
              <BasicLoader />
            </Grid>
          ) : null}
          {adminCourseTutorsData &&
          adminCourseTutorsData.list &&
          adminCourseTutorsData.list.length
            ? adminCourseTutorsData.list.map((item) => (
                <Grid key={`course-tutor-${item._id}`} item xs={6}>
                  <CourseTutorCard
                    courseTutor={item}
                    onEdit={() => openCourseTutorEditModal(item)}
                  />
                </Grid>
              ))
            : null}
          {adminCourseTutorsData && !adminCourseTutorsData.totalDocs ? (
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                textAlign="center"
              >
                No Tutors are available.
              </Typography>
            </Grid>
          ) : null}
        </Grid>
      </div>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        mt="60px"
        spacing="10px"
      >
        <Button
          disableElevation
          variant="contained"
          color="inherit"
          onClick={onClose}
        >
          Dismiss
        </Button>
      </Stack>
      {/* Modals */}
      {isAddCourseTutorModalOpen ? (
        <AddCourseTutorModal
          open={isAddCourseTutorModalOpen}
          onClose={toggleAddCourseTutorModal}
        />
      ) : null}
      {editCourseTutorModalData &&
      editCourseTutorModalData.open &&
      editCourseTutorModalData.courseTutor ? (
        <AddCourseTutorModal
          mode="edit"
          open={editCourseTutorModalData.open}
          onClose={closeCourseTutorEditModal}
          courseTutor={editCourseTutorModalData.courseTutor}
        />
      ) : null}
    </div>
  );
};

export default TutorsTab;
